/**
 * The "packaged" share artwork. Rendered at a fixed 1080x1080 so html-to-image
 * rasterizes a predictable, social-ready square. Deliberately avoids the app's
 * glass-surface utilities: backdrop-filter does not rasterize (it samples what
 * is *behind* the node), so this card uses solid fills and explicit gradients
 * only.
 */
import { forwardRef } from 'react'
import logoUrl from '@/assets/vaagon-logo.svg'
import type { ShareSubject } from '@/lib/share'

export const SHARE_CARD_SIZE = 1080

const FONT_STACK =
  'Figtree, ui-sans-serif, system-ui, -apple-system, sans-serif'

const GOLD_FROM = '#F6CB86'
const GOLD_TO = '#E0A263'

function Wordmark() {
  return (
    <img
      src={logoUrl}
      alt="Vaagon"
      style={{ height: 60, width: 'auto', display: 'block' }}
    />
  )
}

function ScoreRing({ score, label }: { score: number; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
      <div
        style={{
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${GOLD_FROM}, ${GOLD_TO})`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#0b0b10',
        }}
      >
        <span style={{ fontSize: 76, fontWeight: 800, lineHeight: 1 }}>
          {score}
          <span style={{ fontSize: 40 }}>%</span>
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 30, color: 'rgba(255,255,255,0.55)' }}>
          Overall score
        </span>
        <span style={{ fontSize: 44, fontWeight: 700, color: GOLD_FROM }}>
          {label}
        </span>
      </div>
    </div>
  )
}

function StatChip({ value, caption }: { value: string; caption: string }) {
  return (
    <div
      style={{
        flex: 1,
        borderRadius: 24,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(255,255,255,0.04)',
        padding: '28px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <span style={{ fontSize: 52, fontWeight: 800, color: '#ffffff' }}>
        {value}
      </span>
      <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.5)' }}>
        {caption}
      </span>
    </div>
  )
}

/**
 * Fills the stats slot for a freshly submitted idea (no score, no votes).
 * Without it the card reads as empty; this turns the gap into an invitation.
 */
function IdeaInfoPanel() {
  return (
    <div
      style={{
        borderRadius: 28,
        border: `1px solid ${GOLD_TO}40`,
        background:
          'linear-gradient(135deg, rgba(246,203,134,0.1), rgba(224,162,99,0.04))',
        padding: '36px 40px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <span style={{ fontSize: 30, fontWeight: 700, color: GOLD_FROM }}>
        Fresh idea, gathering validation
      </span>
      <span
        style={{
          fontSize: 30,
          lineHeight: 1.45,
          color: 'rgba(255,255,255,0.7)',
        }}
      >
        Vaagon is where founders pressure-test ideas with real community
        feedback on problem, solution, and market before building. Open the link
        to weigh in, or post your own idea at vaagon.ai
      </span>
    </div>
  )
}

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: SHARE_CARD_SIZE,
        height: SHARE_CARD_SIZE,
        boxSizing: 'border-box',
        background:
          'radial-gradient(120% 80% at 80% 0%, rgba(224,162,99,0.18) 0%, rgba(11,11,16,0) 55%), #0b0b10',
        fontFamily: FONT_STACK,
        padding: 72,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        color: '#ffffff',
      }}
    >
      {children}
    </div>
  )
}

/**
 * Footer with the call to action and a QR code. The QR is what makes the
 * rasterized image actionable: on image-only channels (Instagram, a saved
 * download) there is no clickable link, so scanning the code is the way in.
 */
function CardFooter({
  cta,
  qrDataUrl,
}: {
  cta: string
  qrDataUrl?: string | null
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 32,
        borderTop: '1px solid rgba(255,255,255,0.1)',
        paddingTop: 32,
        // The footer (CTA + QR) must never be squeezed off the fixed-height
        // card — pin it so overflowing body content clips itself instead.
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 30, color: 'rgba(255,255,255,0.55)' }}>
          {cta}
        </span>
        <span style={{ fontSize: 32, fontWeight: 700, color: GOLD_FROM }}>
          {qrDataUrl ? 'Scan to open, or visit vaagon.ai' : 'vaagon.ai'}
        </span>
      </div>
      {qrDataUrl && (
        <img
          src={qrDataUrl}
          alt=""
          width={132}
          height={132}
          style={{
            width: 132,
            height: 132,
            borderRadius: 16,
            background: '#ffffff',
            padding: 10,
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        />
      )}
    </div>
  )
}

/**
 * The off-screen artwork node. The modal renders this, scales it down for the
 * preview, and hands the true-size node to html-to-image. `qrDataUrl` is a
 * pre-generated QR PNG; when absent the footer falls back to text only.
 */
export const ShareCard = forwardRef<
  HTMLDivElement,
  { subject: ShareSubject; qrDataUrl?: string | null }
>(function ShareCard({ subject, qrDataUrl }, ref) {
  if (subject.kind === 'idea') {
    // The card is a fixed 1080px export, so a long title can't be allowed to
    // grow the card — instead we shrink the title font as it gets longer and
    // allow proportionally more lines, so the WHOLE title shows without being
    // clipped (it's a share card; readers need the full text).
    const titleLen = subject.title?.length ?? 0
    // The card is a fixed 1080px square, and a scored idea also shows the score
    // ring + stat chips, so the title gets a limited vertical budget. Keep the
    // font small enough that even a two-line title fits with the description and
    // metrics below it — otherwise the body clips the bottom of the text.
    const titleFontSize =
      titleLen <= 32 ? 54 : titleLen <= 58 ? 46 : titleLen <= 90 ? 40 : 34
    const titleLineClamp = titleFontSize >= 54 ? 3 : 4
    return (
      <div ref={ref}>
        <CardShell>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              flexShrink: 0,
            }}
          >
            <Wordmark />
            {subject.rank != null && (
              <span
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  color: GOLD_FROM,
                  border: `1px solid ${GOLD_TO}66`,
                  background: 'rgba(246,203,134,0.1)',
                  borderRadius: 999,
                  padding: '10px 24px',
                  // Keep "Ranked #N" on one line — it was wrapping when the
                  // header row got tight.
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                Ranked #{subject.rank}
              </span>
            )}
          </div>

          {/* Body absorbs the space between the pinned header and footer and
              clips its own overflow, so a long title or pitch can never push
              the footer (and its QR) off the fixed-height card. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 28,
            }}
          >
            {/* Text block clips first: it can shrink and hide its own
                overflow so a long title never pushes the metrics (score +
                chips) out of the card. */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                minHeight: 0,
                flexShrink: 1,
                overflow: 'hidden',
              }}
            >
              <span style={{ fontSize: 30, color: 'rgba(255,255,255,0.5)' }}>
                I am validating my idea on Vaagon
              </span>
              <span
                style={{
                  fontSize: titleFontSize,
                  fontWeight: 800,
                  lineHeight: 1.08,
                  letterSpacing: '-0.02em',
                  // Font size shrinks with length (above); the clamp scales with
                  // it as a final safety bound so the footer/QR stay on-card.
                  display: '-webkit-box',
                  WebkitLineClamp: titleLineClamp,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {subject.title}
              </span>
              {subject.oneLinePitch && (
                <span
                  style={{
                    fontSize: 28,
                    lineHeight: 1.4,
                    color: 'rgba(255,255,255,0.65)',
                    // Bound the pitch so a long one cannot blow out the card.
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {subject.oneLinePitch}
                </span>
              )}
            </div>

            {/* Metrics only render once the idea has them. A freshly
                submitted idea has no score and zero votes; broadcasting
                "Insufficient data" or "0" undersells it, so the stats slot
                falls back to a short explainer + invitation instead. */}
            {subject.overallScore != null || subject.votes > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 32,
                  // Score + chips are the payload — never let them clip.
                  flexShrink: 0,
                }}
              >
                {subject.overallScore != null && (
                  <ScoreRing
                    score={subject.overallScore}
                    label={subject.scoreLabel}
                  />
                )}
                <div style={{ display: 'flex', gap: 24 }}>
                  {subject.votes > 0 && (
                    <StatChip
                      value={subject.votes.toLocaleString()}
                      caption="Community votes"
                    />
                  )}
                  {subject.overallScore != null && (
                    <StatChip
                      value={subject.scoreLabel}
                      caption="Validation strength"
                    />
                  )}
                </div>
              </div>
            ) : (
              <IdeaInfoPanel />
            )}
          </div>

          <CardFooter
            cta="See the full validation report"
            qrDataUrl={qrDataUrl}
          />
        </CardShell>
      </div>
    )
  }

  // Percentile is a non-redundant flex: the rank line above already states
  // the validator count, so the second chip shows standing, not the count.
  const percentile =
    subject.totalValidators > 0
      ? Math.max(1, Math.round((subject.rank / subject.totalValidators) * 100))
      : null

  return (
    <div ref={ref}>
      <CardShell>
        <Wordmark />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <span style={{ fontSize: 32, color: 'rgba(255,255,255,0.5)' }}>
            {subject.userName
              ? `${subject.userName} is on the leaderboard`
              : 'On the Vaagon leaderboard'}
          </span>
          <span
            style={{
              fontSize: 180,
              fontWeight: 800,
              lineHeight: 1,
              background: `linear-gradient(135deg, ${GOLD_FROM}, ${GOLD_TO})`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            #{subject.rank}
          </span>
          <span style={{ fontSize: 40, color: 'rgba(255,255,255,0.7)' }}>
            out of {subject.totalValidators.toLocaleString()} validators
          </span>
        </div>

        <div style={{ display: 'flex', gap: 24 }}>
          {subject.points != null && (
            <StatChip
              value={subject.points.toLocaleString()}
              caption="Community points"
            />
          )}
          {percentile != null && (
            <StatChip
              value={`Top ${percentile}%`}
              caption="Of all validators"
            />
          )}
        </div>

        <CardFooter
          cta="Validate ideas. Climb the leaderboard."
          qrDataUrl={qrDataUrl}
        />
      </CardShell>
    </div>
  )
})
